/**
 * A string that has been normalized to crossword "grid form":
 * uppercase, with spaces stripped. Construct only via `toGridText` /
 * `getLetters` / `getLettersFromText` from `lib/pairings`.
 *
 * Indexing a GridText returns a single-character GridText (still uppercase,
 * still no spaces), so consumers can read letters directly without further
 * `.toUpperCase()` or guards beyond bounds.
 */
export type GridText = string & { readonly __gridText: unique symbol }

export type ListSide = 'A' | 'B'

// Curatorial status for a pairing. Stored, user-controlled. Note that 'used'
// is NOT a status — whether a pair is placed on a grid is a derived signal
// (see isPairingUsed) so it composes orthogonally with the curatorial axis.
// Quality is its own axis too — see PairingRating.
//   none      — ephemeral / unconsidered
//   draft     — noticed and worth a second look
//   approved  — vetted, ready to use
//   rejected  — explicitly declined
export type PairingStatus = 'none' | 'draft' | 'approved' | 'rejected'

// Subjective quality rating for a pairing. Maps to 1–5:
//   1 stretch · 2 so-so · 3 ok · 4 good · 5 great
// Optional — undefined means "not yet rated".
export type PairingRating = 1 | 2 | 3 | 4 | 5

export const PAIRING_RATING_LABELS: Record<PairingRating, string> = {
  1: 'stretch',
  2: 'so-so',
  3: 'ok',
  4: 'good',
  5: 'great',
}

export type ClueStatus = 'final' | 'draft'

export type Word = {
  id: string
  text: string
  list?: ListSide | undefined // undefined = unassigned/unsorted
  groupId?: string | undefined // Links word variants together within the same list (e.g., KNIFE and KNIVES)
  twinGroupId?: string | undefined // Links the same word's groups across lists for grid-suggestion dedup
  tags?: string[] | undefined // free-form tag labels; canonical id IS the label
}

export type Clue = {
  id?: string | undefined
  text: string
  status: ClueStatus
  createdAt?: string | undefined
  updatedAt?: string | undefined
}

export type Pairing = {
  id: string
  wordAId: string
  wordBId: string
  status: PairingStatus
  rating?: PairingRating | undefined
  clue?: Clue | undefined
  clueDrafts?: Clue[] | undefined
  tags?: string[] | undefined // free-form tag labels; canonical id IS the label
}

export type CrosswordData = {
  words: Word[]
  pairings: Pairing[]
  listNames?: { A: string; B: string }
  drafts?: GridDraft[]
  activeDraftId?: string | null
  scratchpadEntries?: ScratchpadEntry[]
  scratchpadGrid?: ConstructorGrid | null
  fragments?: GridFragment[]
  // Tag registry: every tag name that has ever been used or is otherwise
  // surfaced for filter UI. Words and pairings reference tags by label
  // (label IS the canonical id).
  tags?: string[]
}

// Filter state for browsing
export type FilterState = {
  pairingStatuses: PairingStatus[]
  clueStatuses: (ClueStatus | 'empty')[]
  searchQuery: string
}

// Constructor Grid Types
export type GridCell = {
  letterA: string | null  // Letter from List A word
  letterB: string | null  // Letter from List B word
  placementId: string | null  // Links to GridPlacement
  isBlocked: boolean  // Black cell
}

export type GridPlacement = {
  id: string
  pairingId: string | null  // Existing pairing, or null for manual entry
  wordAText: string  // The actual word text (may be new)
  wordBText: string  // The actual word text (may be new)
  row: number
  col: number
  direction: 'across' | 'down'
  isTempA?: boolean | undefined  // Word A is not saved to word list (or is placeholder)
  isTempB?: boolean | undefined  // Word B is not saved to word list (or is placeholder)
  priority?: number | undefined  // Lower = higher priority for print numbering. Unset = not priority.
}

export type ConstructorGrid = {
  width: number
  height: number
  cells: GridCell[][]
  placements: GridPlacement[]
}

export type Conflict = {
  row: number
  col: number
  letterA: { expected: string; found: string }
  letterB: { expected: string; found: string }
  placementIds: string[]  // The placements causing the conflict
}

export type ViewMode = 'side-by-side' | 'overlay' | 'interleaved'

export type ValidPosition = {
  row: number
  col: number
  direction: 'across' | 'down'
  conflicts: Conflict[]
}

// A conflict-free placement option for a specific pair, ranked by how many
// existing placements it intersects.
export type PlacementOption = {
  row: number
  col: number
  direction: 'across' | 'down'
  intersectionCount: number
}

export type FittingPair = {
  // Pairing record UUID when one exists (recorded metadata: status / clue).
  // null when this is an unrecorded combination — the pair is still valid;
  // it just has no metadata yet. Resolve metadata via getPairingByWords.
  pairingId: string | null
  wordAId: string
  wordBId: string
  wordAText: string
  wordBText: string
  validPositionCount: number
  startOffset: number  // How many cells before the selected cell the word starts (0 = starts at selected cell)
}

export type FittingSingleWord = {
  wordId: string
  wordText: string
  list: ListSide
  startOffset: number  // How many cells before the selected cell the word starts
}

export type AutoSuggestion = {
  pairingId: string | null  // null when no metadata record exists yet
  wordAId: string
  wordBId: string
  wordAText: string
  wordBText: string
  row: number
  col: number
  direction: 'across' | 'down'
  intersectionCount: number
  pairingStatus: PairingStatus | null  // null when no metadata record yet
}

export type PreviewPlacementRole = 'target' | 'helper' | 'standalone'

export type PreviewPlacement = {
  wordAText: string
  wordBText: string
  row: number
  col: number
  direction: 'across' | 'down'
  // Used by multi-placement previews (e.g. backwards-search solutions) to
  // visually distinguish the destination ("target") from steps that lead to
  // it ("helper"). Single-placement previews leave this undefined.
  role?: PreviewPlacementRole | undefined
}

// Constraints at a selected cell for filtering words/pairs
export type CellConstraint = {
  direction: 'across' | 'down'
  // Pattern where null means any letter, string means specific letter required
  // Index 0 is the selected cell position
  patternA: (string | null)[]  // Constraint pattern for word A
  patternB: (string | null)[]  // Constraint pattern for word B
  selectedIndex: number  // Which index in pattern corresponds to selected cell
}

// Draft Types
export type GridDraft = {
  id: string
  name: string
  grid: ConstructorGrid
  updatedAt: string
}

export type ScratchpadEntry = {
  id: string
  wordA: string
  wordB: string
  clue: string
}

// A reusable group of placements with relative coordinates (top-left = 0,0).
// Captured from a multi-selection so it can be re-applied as a unit elsewhere.
export type GridFragment = {
  id: string
  name: string
  placements: GridPlacement[]
  width: number
  height: number
  createdAt: string
  updatedAt: string
}
